import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar as CalendarIcon, Clock, Check, X, RefreshCw, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';
import { CodeITTLogo } from '../components/CodeITTLogo';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland',
];

const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const ClassBooking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [assignedTeacher, setAssignedTeacher] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return localDateStr(d);
  });
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);
  const [studentTimezone, setStudentTimezone] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [rescheduleBookingId, setRescheduleBookingId] = useState(null);
  const [localTime, setLocalTime] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'student') { navigate('/login'); return; }
    initData();
  }, [user, navigate]);

  const initData = async () => {
    try {
      const [teacherRes, timezoneRes, bookingsRes] = await Promise.all([
        api.get('/students/assigned-teacher'),
        api.get('/students/timezone'),
        api.get('/students/my-bookings'),
      ]);
      setAssignedTeacher(teacherRes.data);
      const tz = timezoneRes.data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      setStudentTimezone(tz);
      setMyBookings(bookingsRes.data || []);
      const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(localDateStr(tomorrow));
    } catch (error) {  }
    finally { setLoading(false); }
  };

  // Live local time clock
  useEffect(() => {
    const tick = () => {
      setLocalTime(new Date().toLocaleString('en-US', {
        timeZone: studentTimezone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
        weekday: 'short', month: 'short', day: 'numeric'
      }));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [studentTimezone]);

  // Week helpers
  const getWeekDates = (baseDate) => {
    const d = new Date(baseDate);
    const day = d.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(d); monday.setDate(d.getDate() + mondayOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(monday); date.setDate(monday.getDate() + i);
      return localDateStr(date);
    });
  };

  const weekDates = getWeekDates(selectedDate);
  const weekStart = weekDates[0];
  const weekEnd = weekDates[6];

  const shiftWeek = (dir) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + dir * 7);
    setSelectedDate(localDateStr(d));
  };

  const fetchSlots = useCallback(async () => {
    if (!assignedTeacher) return;
    try {
      const res = await api.get('/students/teachers-availability', {
        params: { teacher_id: assignedTeacher.id, start_date: weekStart, end_date: weekEnd },
      });
      setAvailableSlots(res.data || []);
    } catch { setAvailableSlots([]); }
  }, [assignedTeacher, weekStart, weekEnd]);

  useEffect(() => { fetchSlots(); setSelectedSlotIds([]); }, [fetchSlots]);

  const handleTimezoneChange = async (tz) => {
    setStudentTimezone(tz);
    try { await api.patch('/students/update-timezone', { timezone: tz }); } catch {}
  };

  // Generate 24-hour time slots
  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const sh = h.toString().padStart(2, '0'), sm = m.toString().padStart(2, '0');
      const em = m === 30 ? '00' : '30', eh = m === 30 ? (h + 1).toString().padStart(2, '0') : sh;
      timeSlots.push({ start_time: `${sh}:${sm}`, end_time: `${eh}:${em}` });
    }
  }

  const getSlotForCell = (date, startTime) => {
    return availableSlots.find(s => s.date === date && s.start_time === startTime);
  };

  const convertTime = (timeStr, date) => {
    if (studentTimezone === 'UTC') return timeStr;
    try {
      const dt = new Date(`${date || weekStart}T${timeStr}:00Z`);
      return dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: studentTimezone });
    } catch { return timeStr; }
  };

  const toggleSlot = (slot) => {
    const id = slot.id;
    if (selectedSlotIds.includes(id)) {
      setSelectedSlotIds(selectedSlotIds.filter(s => s !== id));
      return;
    }
    if (selectedSlotIds.length >= 2) {
      toast.error('Select exactly 2 consecutive slots for a 1-hour class');
      return;
    }
    const newIds = [...selectedSlotIds, id];
    if (newIds.length === 2) {
      const s1 = availableSlots.find(s => s.id === newIds[0]);
      const s2 = availableSlots.find(s => s.id === newIds[1]);
      if (s1 && s2) {
        if (s1.date !== s2.date) { toast.error('Both slots must be on the same day'); return; }
        if (s1.end_time !== s2.start_time && s2.end_time !== s1.start_time) { toast.error('Slots must be consecutive'); return; }
      }
    }
    setSelectedSlotIds(newIds);
  };

  const handleBookClass = async () => {
    if (selectedSlotIds.length !== 2) { toast.error('Please select 2 consecutive slots'); return; }
    setBookingInProgress(true);
    try {
      if (rescheduleBookingId) {
        await api.post(`/students/bookings/${rescheduleBookingId}/reschedule`, { slot_ids: selectedSlotIds, timezone: studentTimezone });
        toast.success('Class rescheduled!'); setRescheduleBookingId(null);
      } else {
        await api.post('/students/book-class', { slot_ids: selectedSlotIds, timezone: studentTimezone });
        toast.success('Class booked!');
      }
      setSelectedSlotIds([]);
      fetchSlots();
      const bRes = await api.get('/students/my-bookings');
      setMyBookings(bRes.data || []);
    } catch (error) { toast.error(error.response?.data?.detail || 'Booking failed'); }
    finally { setBookingInProgress(false); }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await api.patch(`/students/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      const bRes = await api.get('/students/my-bookings');
      setMyBookings(bRes.data || []);
      fetchSlots();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to cancel'); }
  };

  const handleReschedule = (bookingId) => {
    setRescheduleBookingId(bookingId);
    toast.info('Select 2 new consecutive slots to reschedule');
  };

  const scheduledBookings = myBookings.filter(b => b.status === 'scheduled');
  const pastBookings = myBookings.filter(b => b.status !== 'scheduled');

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <CodeITTLogo size="small" />
            <span className="text-base font-bold text-slate-900">Book a Class</span>
          </div>
          <div className="flex items-center gap-4">
            {/* Live Clock */}
            <div className="hidden md:flex items-center gap-2 bg-violet-50 px-3 py-1.5 rounded-lg border border-violet-200" data-testid="live-clock">
              <Clock className="w-4 h-4 text-violet-600" />
              <span className="text-sm font-mono font-medium text-violet-700">{localTime}</span>
            </div>
            {/* Timezone Selector */}
            <div className="flex items-center gap-1.5">
              <Globe className="w-4 h-4 text-slate-500" />
              <Select value={studentTimezone} onValueChange={handleTimezoneChange}>
                <SelectTrigger className="w-48 border-violet-200 text-sm" data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6 grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Left: Weekly Calendar */}
        <div className="xl:col-span-3 space-y-4">
          {!assignedTeacher ? (
            <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
              <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Teacher Assigned</h3>
              <p className="text-slate-600">Enroll in a course to get a teacher assigned.</p>
            </div>
          ) : (
            <>
              {/* Teacher + Week Nav */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-sm text-slate-500">Teacher: <span className="font-semibold text-slate-900" data-testid="teacher-name">{assignedTeacher.name}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm font-medium text-slate-700 px-2">
                    {new Date(weekStart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(weekEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="next-week"><ChevronRight className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => { const _t = new Date(Date.now() + 86400000); setSelectedDate(localDateStr(_t)); }}>This Week</Button>
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-5 text-xs">
                <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded-lg bg-blue-100 border-2 border-blue-400"></div><span className="font-medium">Recurring</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded-lg bg-orange-100 border-2 border-orange-400"></div><span className="font-medium">Additional</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded-lg bg-violet-500"></div><span className="font-medium">Selected</span></div>
              </div>

              {/* Weekly Grid */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-auto max-h-[560px]">
                  <table className="w-full border-collapse" data-testid="weekly-calendar">
                    <thead className="sticky top-0 z-10 bg-white">
                      <tr>
                        <th className="p-2.5 text-xs font-bold text-slate-500 border-b border-r border-slate-200 w-16 bg-slate-50">Time</th>
                        {weekDates.map(date => {
                          const d = new Date(date + 'T00:00:00');
                          const isToday = date === localDateStr(new Date());
                          return (
                            <th key={date} className={`p-2.5 text-center border-b border-r border-slate-200 ${isToday ? 'bg-violet-50' : 'bg-slate-50'}`}>
                              <div className={`text-xs font-bold ${isToday ? 'text-violet-700' : 'text-slate-500'}`}>{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                              <div className={`text-base font-extrabold ${isToday ? 'text-violet-700' : 'text-slate-900'}`}>{d.getDate()}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {timeSlots.map(ts => (
                        <tr key={ts.start_time}>
                          <td className="px-2 py-0 text-xs text-slate-500 border-r border-b border-slate-100 font-mono whitespace-nowrap bg-slate-50 text-right font-semibold">
                            {convertTime(ts.start_time)}
                          </td>
                          {weekDates.map(date => {
                            const slot = getSlotForCell(date, ts.start_time);
                            const isSelected = slot && selectedSlotIds.includes(slot.id);
                            const isRecurring = slot?.type === 'long_term';
                            if (!slot) return <td key={date} className="border-r border-b border-slate-100 h-8"></td>;
                            return (
                              <td
                                key={date}
                                onClick={() => toggleSlot(slot)}
                                className="border-r border-b border-slate-100 h-8 cursor-pointer transition-colors p-0.5"
                                data-testid={`cell-${date}-${ts.start_time}`}
                              >
                                <div className={`w-full h-full rounded-xl ${
                                  isSelected ? 'bg-violet-500' : isRecurring ? 'bg-blue-100 border border-blue-300 hover:bg-blue-200' : 'bg-orange-100 border border-orange-300 hover:bg-orange-200'
                                }`} />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Selection bar */}
              {selectedSlotIds.length > 0 && (
                <div className="p-4 bg-violet-50 rounded-xl border-2 border-violet-200 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-medium text-violet-900">{selectedSlotIds.length}/2 slots selected</p>
                    <p className="text-sm text-violet-600">
                      {selectedSlotIds.length === 2 ? 'Ready to book your 1-hour class!' : 'Select 1 more consecutive slot'}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setSelectedSlotIds([])} className="border-violet-300 text-violet-700" data-testid="clear-slots-btn">Clear</Button>
                    <Button onClick={handleBookClass} disabled={bookingInProgress || selectedSlotIds.length !== 2} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="book-class-btn">
                      {bookingInProgress ? 'Booking...' : rescheduleBookingId ? 'Confirm Reschedule' : 'Book Class'}
                    </Button>
                  </div>
                </div>
              )}
              {rescheduleBookingId && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center justify-between">
                  <span>Rescheduling mode — pick 2 new consecutive slots</span>
                  <Button variant="ghost" size="sm" onClick={() => setRescheduleBookingId(null)} className="text-amber-700">Cancel Reschedule</Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right: Bookings */}
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Upcoming Classes</h2>
            {scheduledBookings.length === 0 ? (
              <div className="bg-white rounded-xl p-6 text-center border border-slate-200">
                <CalendarIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No upcoming classes</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="upcoming-bookings">
                {scheduledBookings.map(b => (
                  <div key={b.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`booking-${b.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{b.teacher_name}</p>
                        <div className="flex items-center text-xs text-slate-500 gap-1 mt-1">
                          <CalendarIcon className="w-3 h-3" /><span>{b.date}</span>
                          <Clock className="w-3 h-3 ml-2" /><span>{b.start_time} - {b.end_time}</span>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">{b.status}</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => handleReschedule(b.id)} data-testid={`reschedule-${b.id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Reschedule
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleCancelBooking(b.id)} data-testid={`cancel-${b.id}`}>
                        <X className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {pastBookings.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-3">Past / Cancelled</h2>
              <div className="space-y-2">
                {pastBookings.slice(0, 10).map(b => (
                  <div key={b.id} className="bg-white rounded-lg border border-slate-200 p-3 opacity-70">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-slate-700">{b.teacher_name}</p>
                        <p className="text-xs text-slate-500">{b.date} {b.start_time}-{b.end_time}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        b.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        b.status === 'rescheduled' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{b.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassBooking;
